<template>
  <BittsCard :no-padding="false" class="c-incoming-share-request-card">
    <div class="flex w-full justify-between">
      <div class="flex flex-col mr-16 gap-2">
        <BittsAvatarItem size="small" :title :user />
        <p class="text-neutral-text text-sm" data-testid="message">
          {{ incomingShareRequest?.message }}
        </p>
        <p class="text-neutral-text-weak text-xs" data-testid="timeSent">
          Requested {{ timeSent }}
        </p>
      </div>
      <div class="flex gap-8 items-center">
        <BittsButton
          text="Ignore"
          size="x-small"
          type="danger"
          variant="ghost"
          @click="ignoreRequest"
        />
        <BittsButton
          text="Edit sharing"
          size="x-small"
          @click="handleEditSharing"
        />
      </div>
    </div>
  </BittsCard>
</template>
<script setup lang="ts">
import { BittsAvatarItem, BittsButton, BittsCard } from '@crossbeam/bitts';

import { DateTime } from 'luxon';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import { timeSince } from '@/date_time_utils';
import { useDataRequestsStore } from '@/stores';
import { InboundShareRequest } from '@/types/data_share_requests';

const { incomingShareRequest } = defineProps<{
  incomingShareRequest: InboundShareRequest;
}>();

const emits = defineEmits(['ignored-data-request']);

const router = useRouter();
const dataRequestsStore = useDataRequestsStore();

async function ignoreRequest() {
  await dataRequestsStore.ignoreShareRequest(
    incomingShareRequest.id.toString(),
  );
  await dataRequestsStore.refreshDataRequestsStore();
  emits('ignored-data-request');
}

const userName = computed(() =>
  incomingShareRequest?.user_first_name && incomingShareRequest.user_last_name
    ? `${incomingShareRequest.user_first_name} ${incomingShareRequest.user_last_name}`
    : `${incomingShareRequest.user_email}`,
);
const title = computed(
  () => `${userName.value} requested data from this population`,
);
const user = computed(() => ({
  email: incomingShareRequest?.user_email,
}));

const timeSent = computed(() => {
  const date = DateTime.fromISO(incomingShareRequest?.created_at);
  return date.isValid ? timeSince(date) : '';
});

const handleEditSharing = () => {
  router.push({
    name: 'partnership-settings',
    params: {
      partner_org_id: incomingShareRequest.partner_org_id,
      tab: 'sharing-settings',
    },
  });
};
</script>
<style scoped lang="pcss">
@reference "../../css/theme.css";
.c-incoming-share-request-card {
  @apply bg-info-background-weak border-none mt-16 p-12;
}
</style>
